import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SchemaQueryService } from '@app/modules/analytics/services/schema-query.service';
import { DatasetApiService } from '@app/modules/core/services/dataset-api.service';
import { DatasetSubtype, DatasetType, QueryParams } from '@app/modules/core/store/models';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DocumentSchema, FieldDataType, SchemaField } from 'src/app/modules/core/store/models/document.model';
import { FilterGroup, ProximityFilterSettings } from 'src/app/modules/core/store/models/filters.model';
import { Workflow, WorkflowStep } from 'src/app/modules/core/store/models/workflow.model';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { v4 as uuidv4 } from 'uuid';
import { DatasetTypesForDisplay, DatasetSubtypesForDisplay } from 'src/app/modules/management/store/datasets/dataset.model';
import { StatusApiService } from '@app/modules/management/store/status/status-api.service';
import { UserContext } from 'src/app/modules/authentication/store/auth';
import { FieldFilterService } from '@app/modules/core/services/field-filter.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'topo-proximity-filter-settings',
  templateUrl: './proximity-filter-settings.component.html',
  styleUrls: ['./proximity-filter-settings.component.scss'],
  standalone: false
})
export class ProximityFilterSettingsComponent implements OnInit, OnDestroy {
  @Input() isActive: boolean;

  _filterSettings: ProximityFilterSettings;
  @Input() set settings(value: ProximityFilterSettings) {
    this._filterSettings = value;
  }
  @Input() useForDatamart: boolean;
  @Output() cancel = new EventEmitter<void>();
  @Output() changed = new EventEmitter<ProximityFilterSettings>();

  isUserDistanceInKilometers: boolean;
  proximityValue: number;
  selectedDatasetType: string = '1';
  selectedDatasetSubtype: DatasetSubtype = DatasetSubtype.None;
  selectedDatasetUId?: string;
  hasDatasets: boolean = true;
  hasFilter: boolean;
  datasets: any[];
  workflow: Workflow;
  schema: DocumentSchema;
  filterGroup: FilterGroup;
  filterWorkflow: Workflow = null;
  statusWorkflow: Workflow = null;
  settingsFilterGroup: FilterGroup = null;
  datasetTypes: any[] = [];
  distanceMaxValue: number;
  lookupTargetSchemaFields: SchemaField[];
  private userContext: UserContext;
  private destroy$: Subject<void> = new Subject();
  Permissions = fromAuth.Permissions;
  geoFields: any[] = [];
  selectedField: string;

  constructor(
    private store: Store<any>,
    private translate: TranslateService,
    private datasetApiService: DatasetApiService,
    private schemaQueryService: SchemaQueryService,
    private statusApiService: StatusApiService,
    private notify: SystemNotificationsService,
    private changeDetector: ChangeDetectorRef,
    private fieldFilterService: FieldFilterService
  ) {}

  ngOnInit(): void {
    this.store.select(fromUserProfile.selectProfileUser).subscribe(userProfile => {
      this.isUserDistanceInKilometers = userProfile?.culture?.distanceUnit === 'kilometers';
      this.distanceMaxValue = this.isUserDistanceInKilometers ? 161 : 100;
    });

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.userContext = userContext;
        if (this.userContext.hasPermission(this.Permissions.IncidentRead)) {
          let incidentEntry = {
            name: `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + DatasetType.Incident)}`,
            value: `${this.datasetTypes.length + 1}`,
            type: DatasetType.Incident,
            subtype: DatasetSubtype.None
          };
          this.datasetTypes.push(incidentEntry);
        }
      });

    this.loadDatasetTypes();
    this.loadFilterWorkflow();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.proximityValue = this._filterSettings?.withinMeters;
    if (this.proximityValue !== undefined) {
      this.proximityValue = this.isUserDistanceInKilometers === true ? this.proximityValue / 1000 : this.proximityValue / 1609.344;
      this.proximityValue = Math.round(this.proximityValue);
    }
    if (this.proximityValue === undefined) {
      this.proximityValue = 10;
    }

    this.selectedField = this._filterSettings?.geometryField;

    const selectedDatasetRecord = this.datasetTypes?.find(
      t => t.type === this._filterSettings?.datasetType && t.subtype === this._filterSettings?.datasetSubtype
    );
    if (!selectedDatasetRecord) {
      this.selectedDatasetType = '1';
    } else {
      this.selectedDatasetType = selectedDatasetRecord.value;
    }
    this.selectedDatasetUId = this._filterSettings?.datasetUId;
    this.hasDatasets = true;
    this.settingsFilterGroup = this._filterSettings?.filter;
    this.handleDatasetTypeChange();
  }

  private loadDatasetTypes(): void {
    let count = 1;
    const types = DatasetTypesForDisplay?.sort((e1, e2) =>
      this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + e1) > this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + e2)
        ? 1
        : -1
    );

    types.map(t => {
      const subTypes = DatasetSubtypesForDisplay[t]?.sort((e1, e2) =>
        this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-' + e1) >
        this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-' + e2)
          ? 1
          : -1
      );
      subTypes
        ?.filter(s => s !== DatasetSubtype.TropicalCyclone && s !== DatasetSubtype.Weather)
        .map(s => {
          if (s !== DatasetSubtype.None) {
            const typeName = this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + t);
            const subTypeName = this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-' + s);
            let entry = {
              name: `${typeName} | ${subTypeName}`,
              value: `${count}`,
              type: t,
              subtype: s
            };
            count++;
            this.datasetTypes.push(entry);
          } else if (t === DatasetType.Metrics || t === DatasetType.ActivityLog || t === DatasetType.Alarm) {
            const typeName = this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + t);
            let entry = {
              name: `${typeName}`,
              value: `${count}`,
              type: t,
              subtype: s
            };
            count++;
            this.datasetTypes.push(entry);
          }
        });
    });
  }

  datasetTypeChanged(): void {
    this.selectedDatasetUId = null;
    this.filterGroup = null;
    this.handleDatasetTypeChange();
  }

  private handleDatasetTypeChange(): void {
    const selectedDatasetRecord = this.datasetTypes.find(t => t.value === this.selectedDatasetType);

    const datasetType = selectedDatasetRecord?.type ?? DatasetType.None;
    const datasetSubtype = selectedDatasetRecord?.subtype ?? DatasetSubtype.None;

    this.datasets = [{ uId: this.selectedDatasetUId, name: this.translate.instant('FILTERS.LOADING') }];
    this.schema = { fields: [] };

    this.getDatasets(datasetType, datasetSubtype)
      .pipe(takeUntil(this.destroy$))
      .subscribe(datasets => {
        this.datasets = [];
        if (this.useForDatamart) {
          this.datasets.push({
            uId: null,
            name: this.translate.instant('FILTERS.ALL-DATASETS')
          });
        }
        datasets?.map(d => this.datasets.push(d));
        if (!this.useForDatamart && !!!this.selectedDatasetUId && datasets?.length > 0) {
          this.selectedDatasetUId = datasets[0].uId;
        }
        this.handleDatasetChange();
        this.changeDetector.markForCheck();
      });
  }

  datasetChanged(dataset: any): void {
    this.filterGroup = null;
    this.filterWorkflow = null;
    this.settingsFilterGroup = null;
    this.handleDatasetChange();
  }

  private handleDatasetChange(): void {
    const selectedDatasetRecord = this.datasetTypes?.find(t => t.value === this.selectedDatasetType);

    const datasetType = selectedDatasetRecord?.type;
    const datasetSubtype = selectedDatasetRecord?.subtype;

    if (!!this.selectedDatasetUId) {
      const dataset = this.datasets?.find(d => d.uId === this.selectedDatasetUId);
      if (!dataset) {
        this.notify.error(this.translate.instant('FILTERS.ERROR-SELECTED-DATASET'));
        return;
      }
      this.filterWorkflow = dataset?.workflow;
      this.filterGroup = this.settingsFilterGroup;
      this.schema = dataset?.schema;
      this.populateFields();
    } else {
      this.schemaQueryService
        .getSchema(datasetType, datasetSubtype)
        .pipe(takeUntil(this.destroy$))
        .subscribe(schema => {
          if (schema) {
            this.filterWorkflow = this.statusWorkflow;
            this.filterGroup = this.settingsFilterGroup;
            this.schema = schema;
            this.populateFields();
          }
          this.changeDetector.markForCheck();
        });
    }
  }

  private populateFields(): void {
    let fields = this.schema.fields?.sort((a, b) => a.displayName?.localeCompare(b.displayName));

    this.geoFields = fields.filter(f => f.dataType === FieldDataType.Geometry);

    if (!!!this.selectedField || !!!this.geoFields.find(v => v.systemName === this.selectedField)) {
      this.selectedField = this.geoFields?.length > 0 ? this.geoFields[0].systemName : null;
    }
  }

  private getDatasets(type: DatasetType, subtype: DatasetSubtype): Observable<any> {
    const queryParams: QueryParams = {
      skip: 0,
      take: 200,
      sortField: 'Name',
      sortDirection: 'Ascending',
      where: this.useForDatamart
        ? `type=${type} AND subType=${subtype} AND IncludeInDataMart==true AND IsActive==true`
        : `type=${type} AND subType=${subtype} AND IsActive==true`
    };

    return this.datasetApiService.getDatasets(queryParams, true, true).pipe(
      catchError(e => {
        console.error(e);
        return of(null);
      }),
      map(res => {
        return res?.data;
      })
    );
  }

  private loadFilterWorkflow(): void {
    const queryParams: QueryParams = {
      skip: 0,
      take: 500,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };

    this.statusApiService.getStatuses(queryParams, false).subscribe(action => {
      let workflow = {
        uId: uuidv4(),
        name: 'Workflow for filter',
        workflowSteps: []
      } as Workflow;

      action.data.map(status => {
        const step = {
          uId: status.uId,
          name: status.name,
          workflowStatusUId: status.uId,
          statusName: status.name,
          statusHtmlColorCode: status.htmlColorCode
        } as WorkflowStep;
        workflow.workflowSteps.push(step);
      });

      this.statusWorkflow = workflow;
    });
  }

  filterChanged(updatedFilterGroup: FilterGroup) {
    this.filterGroup = updatedFilterGroup;
  }

  cancelClick(): void {
    this.cancel.emit();
  }

  saveClick(): void {
    const filterSettings = {} as ProximityFilterSettings;

    if (this.isUserDistanceInKilometers === true) {
      filterSettings.withinMeters = this.proximityValue * 1000;
    } else {
      filterSettings.withinMeters = Math.round(this.proximityValue * 1609.344);
    }
    const selectedDatasetRecord = this.datasetTypes.find(t => t.value === this.selectedDatasetType);

    filterSettings.datasetUId = this.selectedDatasetUId;
    filterSettings.datasetType = selectedDatasetRecord.type;
    filterSettings.datasetSubtype = selectedDatasetRecord.subtype;
    filterSettings.filter = this.filterGroup;
    filterSettings.geometryField = this.selectedField;

    if (!!this.selectedDatasetUId) {
      const dataset = this.datasets?.find(d => d.uId === this.selectedDatasetUId);
      filterSettings.display = dataset?.name;
      filterSettings.systemDataTableName = dataset?.systemDataTableName;
    } else {
      filterSettings.display = selectedDatasetRecord.name;
    }

    filterSettings.schema = { fields: [] };

    // include in schema only fields for filter
    if (!!this.filterGroup) {
      this.schema.fields.forEach(f => {
        if (!!this.fieldFilterService.findFilterByField(this.filterGroup, f.systemName)) {
          filterSettings.schema.fields.push(f);
        }
      });
    }

    this.changed.emit(filterSettings);
  }
}
